/**
 * FlashBook layout fixtures — test-only data for the browser-driven layout
 * tests (see `e2e-web/`).
 *
 * The studio and the player are both behind `requireAuth`, and a book worth
 * measuring needs every block type filled in, so the layout tests render these
 * fixtures instead of driving the creator flow. Each block carries content long
 * enough for a clipped label, a truncated prompt or an overflowing option row to
 * actually happen — that is the point of the fixture, not realism.
 *
 * Only reachable from a dev build: the route that renders them is registered
 * behind `IS_DEV`, alongside the other test-only screens.
 */
import {type ClassPlay} from '#/lib/flashbookClassReport'
import {
  beginRead,
  emptyProgress,
  finishRead,
  type FlashBookReadProgress,
  saveReadPosition,
} from '#/lib/flashbookProgress'
import {getSlideMaxPoints, NON_SCORED_TYPES} from '#/lib/scoring'
import {kindSpeaks} from '#/lib/spokenKinds'
import {type Slide, type SlideType} from '#/types/capsule'
import {
  type FlashBookModel,
  type FlashBookPage,
  type FlashBookPageResult,
} from '#/types/flashbook'

/** A rich reading block's presentation, as addressed by the fixture. */
export type FixtureRichKind = Exclude<NonNullable<Slide['contentKind']>, 'text'>

/**
 * A block the fixture can build: a plain slide type, or one of the rich reading
 * blocks, which are `content` plus a presentation (`content:quote`).
 */
export type FixtureBlockSpec = SlideType | `content:${FixtureRichKind}`

/**
 * Offline placeholder art. A remote URL would make the layout of an image block
 * depend on the network, which is exactly the kind of flake these tests can't
 * afford.
 */
const PLACEHOLDER_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="480" height="270">' +
    '<rect width="480" height="270" fill="#1e3a8a"/>' +
    '<circle cx="240" cy="135" r="62" fill="#f59e0b"/>' +
    '<text x="240" y="248" font-family="sans-serif" font-size="26" fill="#ffffff" text-anchor="middle">Fixture art</text>' +
    '</svg>',
)}`

/**
 * The fixture book, page by page. Reading and word-level blocks first, then the
 * question types, so no page has to scroll an unreasonable distance to reach
 * its last block.
 */
export const FIXTURE_PAGES: {title: string; types: FixtureBlockSpec[]}[] = [
  {
    title: 'Warm up',
    types: ['content', 'fill_blanks', 'mark_words', 'drag_words'],
  },
  {
    title: 'Practice',
    types: [
      'single_choice',
      'multiple_choice',
      'true_false',
      'image_choice',
      'matching',
      'ordering',
      'accordion',
      'fill_blank',
      'complex_fill_blank',
      'find_words',
    ],
  },
  {
    // The rich reading blocks sit on a page of their own: they are the blocks
    // whose *theme treatment* differs most, so each one needs to be reachable in
    // the player without answering a page of questions first.
    title: 'Rich reading',
    types: [
      'content:quote',
      'content:callout',
      'content:caption',
      'content:table',
      'content:formula',
    ],
  },
  {
    // A long page whose *last* block is a question, and one a test can answer:
    // the case where answering ends the page instead of revealing the next
    // question. The two pages above end in a drag and a word search, and the
    // reading page ends in blocks that are never answered at all — so without
    // this page the page's own turn has nowhere to be measured.
    title: 'Page end',
    types: [
      'content',
      'content:quote',
      'content:callout',
      'content:caption',
      'content:table',
      'content:formula',
      'single_choice',
    ],
  },
]

/** The slide type a fixture spec renders as (`content:quote` → `content`). */
export function fixtureSpecType(spec: FixtureBlockSpec): SlideType {
  return spec.startsWith('content:') ? 'content' : (spec as SlideType)
}

/**
 * Every block type the fixture covers, deduplicated. A unit test asserts this is
 * exactly the set the studio editor and the player can both render, so a newly
 * supported block type can't quietly go unmeasured.
 */
export const FIXTURE_BLOCK_TYPES: SlideType[] = Array.from(
  new Set(FIXTURE_PAGES.flatMap(page => page.types.map(fixtureSpecType))),
)

/** Every block the fixture builds, as addressed by the fixtures screen. */
export const FIXTURE_BLOCK_SPECS: FixtureBlockSpec[] = Array.from(
  new Set(FIXTURE_PAGES.flatMap(page => page.types)),
)

/**
 * A word search whose targets are really in the grid — placeable words are what
 * make the block render a solvable board rather than an empty one.
 */
const FIXTURE_GRID: string[][] = [
  ['B', 'O', 'O', 'K', 'A', 'B', 'C', 'D'],
  ['E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'],
  ['P', 'A', 'G', 'E', 'M', 'N', 'O', 'P'],
  ['Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X'],
  ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'],
  ['I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'],
  ['Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X'],
  ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'],
]

/** A rich reading block, filled with representative content. */
export function buildFixtureRichBlock(
  kind: FixtureRichKind,
  id = `fx-content:${kind}`,
): Slide {
  const base: Slide = {
    id,
    type: 'content',
    question: '',
    points: 10,
    contentKind: kind,
  }

  switch (kind) {
    case 'quote':
      return {
        ...base,
        contentText:
          'A book is a dream that you hold in your hand, and every page turns it ' +
          'a little further into the world.',
        contentAuthor: 'Neil Gaiman',
      }
    case 'callout':
      return {
        ...base,
        contentTitle: 'Careful',
        contentText:
          'Read the whole question before you answer it — the last line is the ' +
          'one that tells you what to do.',
        calloutTone: 'warning',
      }
    case 'caption':
      return {
        ...base,
        imageUrl: PLACEHOLDER_IMAGE,
        contentTitle: 'Fixture art',
        contentText:
          'A stand-in illustration, long enough to wrap onto a second line.',
        contentAuthor: 'Fixture credit',
      }
    case 'table':
      return {
        ...base,
        tableRows: [
          ['Planet', 'Moons', 'Day length'],
          ['Mercury', '0', '59 days'],
          ['Earth', '1', '24 hours'],
          ['Mars', '2', '25 hours'],
        ],
        tableCols: 3,
      }
    // formula
    default:
      return {
        ...base,
        // LaTeX, not a string of unicode: a fraction and a root, which is what
        // the block typesets and what a flat string cannot draw.
        latex: 'x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}',
        contentText: 'The quadratic formula, for any a ≠ 0',
      }
  }
}

/** One block of the given spec, filled with representative content. */
export function buildFixtureBlock(
  spec: FixtureBlockSpec,
  id = `fx-${spec}`,
): Slide {
  if (spec.startsWith('content:')) {
    return buildFixtureRichBlock(
      spec.slice('content:'.length) as FixtureRichKind,
      id,
    )
  }
  const type = spec as SlideType
  const base: Slide = {id, type, question: '', points: 10}

  switch (type) {
    case 'content':
      return {
        ...base,
        contentTitle: 'How this book works',
        contentText:
          'Every page is a stack of blocks. Read the page, answer what it asks, ' +
          'and the book keeps going — your score is collected at the end.',
      }
    case 'accordion':
      return {
        ...base,
        question: 'Open a section',
        accordionItems: [
          {
            id: 'ai_0',
            title: 'What is a flashbook?',
            body: 'A short interactive book: pages of reading and practice, played one block at a time.',
          },
          {
            id: 'ai_1',
            title: 'Can I go back a page?',
            body: 'Yes. Every page keeps its place, so you can revisit a block before you continue.',
          },
        ],
      }
    case 'fill_blanks':
      return {
        ...base,
        question: 'Complete the sentence',
        fillBlanksParagraph:
          'The sun rises in the ___ and sets in the ___, twice every ___ day.',
        fillBlanksAnswers: [
          {answer: 'east'},
          {answer: 'west'},
          {answer: 'single'},
        ],
      }
    case 'fill_blank':
      return {
        ...base,
        question: 'Fill in the missing word',
        blanks: [
          {
            id: 'b_0',
            before: 'The capital of France is ',
            answer: 'Paris',
            after: ', on the river Seine.',
          },
        ],
      }
    case 'complex_fill_blank':
      return {
        ...base,
        question: 'Choose the right word for each gap',
        complexBlanks: [
          {
            id: 'cb_0',
            sentence: 'Water boils at ___ degrees Celsius at sea level.',
            answer: '100',
            options: ['50', '100', '150'],
            alternatives: ['one hundred'],
          },
          {
            id: 'cb_1',
            sentence: 'The longest river in the world is the ___.',
            answer: 'Nile',
            options: ['Nile', 'Amazon', 'Congo'],
            alternatives: [],
          },
        ],
      }
    case 'drag_words':
      return {
        ...base,
        question: 'Drag the words into the gaps',
        dragWordsTemplate:
          'The [sun] rose over the [ocean] while the [birds] sang.',
        dragWordsBank: ['moon', 'desert'],
      }
    case 'single_choice':
      return {
        ...base,
        question: 'Which city is the capital of France?',
        options: [
          {id: 'o_0', label: 'Paris', isCorrect: true},
          {id: 'o_1', label: 'Lyon', isCorrect: false},
          {
            id: 'o_2',
            label: 'Marseille and its very long harbour district',
            isCorrect: false,
          },
        ],
      }
    case 'multiple_choice':
      return {
        ...base,
        question: 'Which of these are prime numbers?',
        options: [
          {id: 'o_0', label: '2', isCorrect: true},
          {id: 'o_1', label: '7', isCorrect: true},
          {id: 'o_2', label: '9', isCorrect: false},
          {id: 'o_3', label: '15', isCorrect: false},
        ],
      }
    case 'true_false':
      return {
        ...base,
        question: 'The Pacific is the largest ocean on Earth.',
        answer: 'true',
      }
    case 'image_choice':
      return {
        ...base,
        question: 'Which one is a triangle?',
        options: [
          {
            id: 'o_0',
            label: 'Triangle',
            isCorrect: true,
            imageUrl: PLACEHOLDER_IMAGE,
          },
          {
            id: 'o_1',
            label: 'Square',
            isCorrect: false,
            imageUrl: PLACEHOLDER_IMAGE,
          },
        ],
      }
    case 'matching':
      return {
        ...base,
        question: 'Match each country with its capital',
        pairs: [
          {id: 'p_0', left: 'France', right: 'Paris'},
          {id: 'p_1', left: 'Japan', right: 'Tokyo'},
          {id: 'p_2', left: 'Egypt', right: 'Cairo'},
        ],
      }
    case 'ordering':
      return {
        ...base,
        question: 'Put the steps in order',
        orderItems: [
          {id: 'oi_0', label: 'Open the book'},
          {id: 'oi_1', label: 'Read the page'},
          {id: 'oi_2', label: 'Answer the questions'},
        ],
        correctOrder: ['oi_0', 'oi_1', 'oi_2'],
      }
    case 'mark_words':
      return {
        ...base,
        question: 'Mark every adjective',
        markWordsText:
          'The quick brown fox jumps over the lazy dog while the tired cat sleeps.',
        markWordsCorrect: ['quick', 'lazy', 'tired'],
      }
    case 'find_words':
      return {
        ...base,
        question: 'Find the hidden words',
        findWordsGrid: FIXTURE_GRID,
        findWordsTargets: ['BOOK', 'PAGE'],
      }
    default:
      return base
  }
}

/**
 * The fixture book's pages, built from FIXTURE_PAGES.
 *
 * Block ids carry the page index as well as the spec, so the same block type can
 * appear on two pages — which the rich reading page does — without colliding.
 */
export function buildFixturePages(): FlashBookPage[] {
  return FIXTURE_PAGES.map((page, index) => ({
    id: `fixture-page-${index + 1}`,
    title: page.title,
    blocks: page.types.map(spec =>
      buildFixtureBlock(spec, `fx-${index + 1}-${spec}`),
    ),
  }))
}

/**
 * A one-block book, for measuring a single block in the player.
 *
 * The reader shows one block at a time — the next only appears once the current
 * one is answered — so a spec that needs to see every block type has to load it
 * on its own rather than answer its way through a page.
 */
export function buildSingleBlockFixture(
  spec: FixtureBlockSpec,
  themeId = 'storybook',
): FlashBookModel {
  return {
    ...buildFlashBookFixture(themeId),
    id: `fixture-flashbook-${spec}`,
    title: spec,
    pages: [
      {
        id: `fixture-page-${spec}`,
        title: spec,
        blocks: [buildFixtureBlock(spec, `fx-single-${spec}`)],
      },
    ],
  }
}

/**
 * Content that is *hard* to lay out: right-to-left Arabic, accented French, and
 * passages long enough to wrap well past the column, in the block types where a
 * theme's treatment is most likely to break (a quote's rule, a table's cells, a
 * formula's plate, a question's options).
 *
 * The layout suite runs every theme over these pages, so "does Heritage survive
 * a long Arabic quotation" is a test result rather than an opinion.
 */
const ARABIC_QUOTE =
  'القراءة رحلة لا تنتهي، وكل صفحة نطويها تفتح أمامنا عالمًا جديدًا من المعرفة ' +
  'والفهم، وتعلّمنا أن السؤال الجيد أهم من الجواب السريع.'
const ARABIC_ATTRIBUTION = 'مقولة تراثية'
const FRENCH_CAPTION =
  'Une illustration de démonstration, accompagnée d’une légende assez longue ' +
  'pour passer à la ligne suivante dans toutes les mises en page.'
const LONG_PARAGRAPH =
  'This paragraph exists to be awkward. It runs well past the width of a phone ' +
  'column, contains a deliberately long unbroken word (antidisestablishmentarianism) ' +
  'and a run of short words, so any block that clips or scrolls its own text is ' +
  'caught here rather than in a creator’s book.\n\nA second paragraph proves the ' +
  'theme keeps its line rhythm when the prose runs on: ' +
  'the ruling of an exercise page still has to close each line, and a flourished ' +
  'rule still has to sit between sections rather than through them.'

export const STRESS_PAGES: {title: string; types: FixtureBlockSpec[]}[] = [
  {
    title: 'ضغط القراءة',
    types: ['content', 'content:quote', 'content:callout', 'content:caption'],
  },
  {
    title: 'Contenu long',
    types: ['content:table', 'content:formula', 'single_choice', 'fill_blanks'],
  },
]

/** One stress block, chosen by the spec it plays as. */
export function buildStressBlock(spec: FixtureBlockSpec): Slide {
  const base: Slide = {id: `fx-stress-${spec}`, type: 'content', points: 10}

  if (spec === 'content') {
    return {
      ...base,
      contentTitle: LONG_PARAGRAPH.split('.')[0] + '.',
      contentText: LONG_PARAGRAPH,
    }
  }
  if (spec === 'content:quote') {
    return {
      ...base,
      contentKind: 'quote',
      contentText: ARABIC_QUOTE,
      contentAuthor: ARABIC_ATTRIBUTION,
    }
  }
  if (spec === 'content:callout') {
    return {
      ...base,
      contentKind: 'callout',
      contentTitle: 'معلومة',
      contentText: ARABIC_QUOTE,
      calloutTone: 'info',
    }
  }
  if (spec === 'content:caption') {
    return {
      ...base,
      contentKind: 'caption',
      imageUrl: PLACEHOLDER_IMAGE,
      contentTitle: 'Illustration de démonstration',
      contentText: FRENCH_CAPTION,
      contentAuthor: '© Musée national des sciences et des techniques',
    }
  }
  if (spec === 'content:table') {
    return {
      ...base,
      contentKind: 'table',
      tableRows: [
        ['Planet', 'Number of confirmed moons', 'Length of a solar day'],
        ['Mercury', '0', '58.6 Earth days'],
        ['Venus', '0', '243 Earth days, retrograde'],
        ['Earth', '1', '23 hours, 56 minutes and 4 seconds'],
      ],
      tableCols: 3,
    }
  }
  if (spec === 'content:formula') {
    return {
      ...base,
      contentKind: 'formula',
      // A fraction *inside* a fraction, which is the case the typesetter exists
      // for: there is no arrangement of characters that draws this one.
      latex: '\\frac{\\frac{a}{b}}{\\frac{c}{d}} = \\frac{ad}{bc}',
      contentText: 'Une fraction de fractions, valable tant que b, c et d ≠ 0',
    }
  }
  if (spec === 'single_choice') {
    return {
      ...base,
      type: 'single_choice',
      question:
        'Which of these descriptions of the water cycle is the most accurate?',
      options: [
        {
          id: 'o_0',
          label:
            'Water evaporates, condenses into clouds, falls as precipitation and returns to the ocean',
          isCorrect: true,
        },
        {
          id: 'o_1',
          label: 'Water only moves when it rains hard enough to form a river',
          isCorrect: false,
        },
        {
          id: 'o_2',
          label:
            'L’eau ne se déplace jamais : elle reste exactement où elle se trouve',
          isCorrect: false,
        },
      ],
    }
  }
  // fill_blanks — the answers here are long enough to test the field sizing.
  return {
    ...base,
    type: 'fill_blanks',
    question: 'Complete the sentence',
    fillBlanksParagraph:
      'The longest river in the world is the ___ and it flows through ___.',
    fillBlanksAnswers: [
      {answer: 'Nile', alternatives: ['River Nile', 'the Nile']},
      {answer: 'north-eastern Africa', alternatives: ['North East Africa']},
    ],
  }
}

/** The stress book's pages. */
export function buildStressPages(): FlashBookPage[] {
  return STRESS_PAGES.map((page, index) => ({
    id: `stress-page-${index + 1}`,
    title: page.title,
    blocks: page.types.map(spec => buildStressBlock(spec)),
  }))
}

/*
 * The Arabic book.
 *
 * Not a stress book with Arabic in it: a book *written* in Arabic, so that the
 * direction of a page is the direction of its writing rather than a mixed
 * paragraph's majority. Every string here is a real one a teacher would type —
 * a passage, a question, its options, a pair, a sentence with blanks — because
 * direction is a property of whole blocks, and a block whose writing is only a
 * label never exercises the layout at all.
 *
 * English creeps into one place on purpose: a real Arabic lesson names things
 * in another script, and a page that flips to English because of one term is
 * exactly the bug this fixture exists to catch.
 */
const ARABIC_PASSAGE =
  'تحدث عملية البناء الضوئي في أوراق النبات، حيث يمتص اليخضور ضوء الشمس ' +
  'ويستخدمه لتحويل الماء وثاني أكسيد الكربون إلى غذاء يخزّن على شكل سكر.'
const ARABIC_QUESTION =
  'أي العبارات التالية تصف عملية البناء الضوئي وصفًا دقيقًا؟'
const ARABIC_EXPLANATION =
  'يحوّل النبات الطاقة الضوئية إلى طاقة كيميائية مخزّنة في السكر.'
const ARABIC_WORDS = ['الشمس', 'الماء', 'الأوراق', 'الغذاء']
const ARABIC_SENTENCE = 'يحدث البناء الضوئي في ___ حيث يمتص ___ ضوء الشمس.'
const ARABIC_PAIRS = [
  {left: 'فرنسا', right: 'باريس'},
  {left: 'اليابان', right: 'طوكيو'},
  {left: 'مصر', right: 'القاهرة'},
]

function arabicBlock(spec: string): Slide {
  const id = `fx-ar-${spec}`
  if (spec === 'reading') {
    return {
      id,
      type: 'content',
      contentTitle: 'البناء الضوئي',
      contentText: ARABIC_PASSAGE,
      points: 0,
    }
  }
  if (spec === 'quote') {
    return {
      id,
      type: 'content',
      contentKind: 'quote',
      contentText:
        'العلم نور، والجهل ظلام، ومن سار على الدرب وصل إلى ما يريد بإذن الله.',
      contentAuthor: 'مقولة تراثية',
      points: 0,
    }
  }
  if (spec === 'choice') {
    return {
      id,
      type: 'single_choice',
      question: ARABIC_QUESTION,
      explanation: ARABIC_EXPLANATION,
      options: [
        {
          id: 'a0',
          label:
            'يمتص اليخضور ضوء الشمس ويحوّل الماء وثاني أكسيد الكربون إلى سكر',
          isCorrect: true,
        },
        {id: 'a1', label: 'يحدث في الجذور عند الليل فقط', isCorrect: false},
        {id: 'a2', label: 'يستهلك الأكسجين وينتج الماء', isCorrect: false},
      ],
      points: 10,
    }
  }
  if (spec === 'blanks') {
    return {
      id,
      type: 'fill_blanks',
      question: 'أكمل الجملة بما يناسبها',
      fillBlanksParagraph: ARABIC_SENTENCE,
      fillBlanksAnswers: [
        {answer: 'الأوراق', alternatives: ['أوراق النبات']},
        {answer: 'اليخضور'},
      ],
      points: 10,
    }
  }
  if (spec === 'mark_words') {
    return {
      id,
      type: 'mark_words',
      question: 'حدّد الكلمات التي تعبّر عن عناصر البناء الضوئي',
      markWordsText:
        'تستهلك النباتات الماء وضوء الشمس لإنتاج الغذاء في الأوراق.',
      markWordsCorrect: ['الماء', 'الشمس', 'الأوراق'],
      points: 10,
    }
  }
  if (spec === 'matching') {
    return {
      id,
      type: 'matching',
      question: 'صل كل دولة بعاصمتها',
      pairs: ARABIC_PAIRS.map((pair, i) => ({id: `p${i}`, ...pair})),
      points: 10,
    }
  }
  if (spec === 'ordering') {
    return {
      id,
      type: 'ordering',
      question: 'رتّب عناصر البناء الضوئي',
      orderItems: ARABIC_WORDS.map((label, i) => ({id: `o${i}`, label})),
      correctOrder: ARABIC_WORDS.map((_, i) => `o${i}`),
      points: 10,
    }
  }
  if (spec === 'true_false') {
    return {
      id,
      type: 'true_false',
      question: 'البناء الضوئي يحتاج إلى ضوء الشمس لكي يحدث.',
      answer: 'true',
      explanation: ARABIC_EXPLANATION,
      points: 5,
    }
  }
  // accordion
  return {
    id,
    type: 'accordion',
    question: 'مراحل الدرس',
    accordionItems: [
      {id: 's0', title: 'المقدمة', body: ARABIC_PASSAGE},
      {id: 's1', title: 'الخلاصة', body: ARABIC_EXPLANATION},
    ],
    points: 0,
  }
}

/** The pages of the Arabic book, in the order a lesson would set them. */
export const ARABIC_PAGES: {title: string; types: string[]}[] = [
  {title: 'مقدمة الدرس', types: ['reading', 'quote']},
  {title: 'تمرين', types: ['choice', 'blanks']},
  {
    title: 'تدريب',
    types: ['matching', 'ordering', 'mark_words', 'true_false', 'accordion'],
  },
]

/** Pages of the Arabic book. */
export function buildArabicPages(): FlashBookPage[] {
  return ARABIC_PAGES.map((page, index) => ({
    id: `arabic-page-${index + 1}`,
    title: page.title,
    blocks: page.types.map(arabicBlock),
  }))
}

/**
 * The Arabic book: what a page written right-to-left is measured against.
 */
export function buildArabicFixture(themeId = 'notebook'): FlashBookModel {
  return {
    ...buildFlashBookFixture(themeId),
    id: 'fixture-flashbook-arabic',
    title: 'البناء الضوئي — كتاب تجريبي',
    pages: buildArabicPages(),
  }
}

/**
 * The stress book: awkward content in every theme, for the layout suite.
 */
export function buildStressFixture(themeId = 'storybook'): FlashBookModel {
  return {
    ...buildFlashBookFixture(themeId),
    id: 'fixture-flashbook-stress',
    title: 'Awkward content',
    pages: buildStressPages(),
  }
}

/**
 * A stand-in reading clip, for the suite that measures a block that has one.
 *
 * What that suite measures is the *presence* of a recording — the player, the
 * transport, and the actions that only exist once a block has a take — plus the
 * taller block card all of that arrives in, which is the case a card's bounded
 * editor region actually overflows.
 *
 * It has to be a clip that really decodes, though, and it is carried inline rather
 * than pointed at a URL. A five-millisecond WAV data URI is silent and loads
 * synchronously, where a URL that 404s renders the player's failure state — a
 * different height from run to run, which is how a layout assertion becomes
 * flaky rather than wrong. Eight-bit unsigned PCM, 8 kHz, mono.
 */
export const FIXTURE_READING_AUDIO =
  'data:audio/wav;base64,UklGRkwAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YSgAAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA'

/**
 * A take the desk cannot read, because there is nothing at the other end of it.
 *
 * `.invalid` is reserved and never resolves, so this is offline-stable, and it
 * is the case that used to be reported as *"the browser could not decode it"*:
 * a clip that never arrived, wearing the same sentence as a clip the browser has
 * no decoder for. `?audio=broken` is what a test opens the desk with to check
 * that the notice says the right thing.
 */
export const FIXTURE_UNREADABLE_AUDIO = 'https://flashbook.invalid/reading.m4a'

/**
 * The same book, with a recorded reading on every block that can carry one.
 *
 * A narration is offered on reading blocks only — a table and a formula are
 * notation, and the studio's content editor hides the audio half for them for the
 * same reason.
 *
 * `audio` overrides the clip itself, which is how the unreadable take above gets
 * onto a block.
 */
export function withReadingAudio(
  book: FlashBookModel,
  audio: string = FIXTURE_READING_AUDIO,
): FlashBookModel {
  return {
    ...book,
    pages: book.pages.map(page => ({
      ...page,
      blocks: page.blocks.map(block =>
        // Asked of the kinds rather than restated, so the fixture puts a voice on
        // exactly the blocks that can hold one — including any kind added later
        // (see `#/lib/richBlockKinds`).
        block.type === 'content' && kindSpeaks(block.contentKind ?? 'text')
          ? {...block, readingAudioUrl: audio}
          : block,
      ),
    })),
  }
}

/**
 * A real, decodable clip at a chosen level, as a data URI.
 *
 * Generated rather than pasted, because the *level* is the whole point of this
 * fixture and a wall of base64 is exactly the thing that hides it: the amplitude
 * argument is the fixture's entire content, so it is the thing on screen.
 *
 * 16-bit PCM, 8 kHz, mono — the smallest thing a decoder will take. A tone
 * rather than speech, because the loudness meter cannot tell the difference: it
 * measures energy, and a phrase of voice and a tone of the same level are the
 * same number of LUFS. One second, so the gated block averages have something to
 * average over.
 *
 * `btoa` is deliberately not used. This module is reachable from the studio on a
 * device, and a global that exists only in a browser is exactly the kind of
 * thing that works in every test and crashes on the phone.
 */
export function fixtureReadingAudioAtLevel(
  amplitude: number,
  seconds = 1,
): string {
  const rate = 8000
  const frames = Math.round(rate * seconds)
  const bytes = new Uint8Array(44 + frames * 2)
  const view = new DataView(bytes.buffer)
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) bytes[offset + i] = text.charCodeAt(i)
  }
  ascii(0, 'RIFF')
  view.setUint32(4, 36 + frames * 2, true)
  ascii(8, 'WAVE')
  ascii(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true) // PCM
  view.setUint16(22, 1, true) // mono
  view.setUint32(24, rate, true)
  view.setUint32(28, rate * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  ascii(36, 'data')
  view.setUint32(40, frames * 2, true)
  for (let i = 0; i < frames; i++) {
    const sample = amplitude * Math.sin((2 * Math.PI * 220 * i) / rate)
    view.setInt16(44 + i * 2, Math.round(sample * 0x7fff), true)
  }
  return `data:audio/wav;base64,${base64(bytes)}`
}

const BASE64_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'

/** Standard base64, written out rather than taken from a browser global. */
function base64(bytes: Uint8Array): string {
  let out = ''
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]
    const b = bytes[i + 1]
    const c = bytes[i + 2]
    out += BASE64_ALPHABET[a >> 2]
    out += BASE64_ALPHABET[((a & 0x03) << 4) | ((b ?? 0) >> 4)]
    out +=
      b === undefined
        ? '='
        : BASE64_ALPHABET[((b & 0x0f) << 2) | ((c ?? 0) >> 6)]
    out += c === undefined ? '=' : BASE64_ALPHABET[c & 0x3f]
  }
  return out
}

/**
 * A book whose readings were recorded at wildly different levels.
 *
 * The case level matching exists for, and one a creator meets constantly: page 1
 * close to the microphone, page 2 across the room. Alternating rather than
 * randomly assigned, so a test can assert *which* pages move without depending
 * on how many reading blocks the fixture happens to have.
 *
 * Loud first, then quiet, so the median target sits between the two and the
 * book's own overall level is left roughly where it was.
 */
export function withLevelledReadings(book: FlashBookModel): FlashBookModel {
  const loud = fixtureReadingAudioAtLevel(0.5)
  const quiet = fixtureReadingAudioAtLevel(0.06)
  let reading = 0
  return {
    ...book,
    pages: book.pages.map(page => ({
      ...page,
      blocks: page.blocks.map(block => {
        if (
          block.type !== 'content' ||
          block.contentKind === 'table' ||
          block.contentKind === 'formula'
        ) {
          return block
        }
        const clip = reading % 2 === 0 ? loud : quiet
        reading += 1
        return {...block, readingAudioUrl: clip}
      }),
    })),
  }
}

/**
 * Where a fixture cover film points.
 *
 * Deliberately a path the dev server does not serve on its own: the gates that
 * use it are about *which minute* a cover opens on and how long its window is,
 * both read from the film's length rather than its pixels, and a suite cannot
 * hand a browser a real minute-long video to decode. The specs route the bytes
 * in when the film's own behaviour is what is being measured (`e2e-web/film.ts`).
 */
export const FIXTURE_COVER_FILM_URI = '/fixture-cover-film.mp4'

/**
 * Where a fixture film cover's *still* points.
 *
 * A path the dev server does not serve, like the film's own: the gate that uses it
 * is about *which picture a card paints and where it came from*, so the bytes are
 * routed in by the spec and the URL is what the assertion names.
 */
export const FIXTURE_COVER_POSTER_URI = '/fixture-cover-poster.png'

/**
 * The same fixture book, wearing a film cover that opens at `videoStartMs`.
 *
 * A film cover is the one cover kind whose *playback* is the feature — the
 * creator chose a minute of it and the learner is meant to see that minute — and
 * until now the only surface a browser could reach had a still image on it, so
 * everything about how a cover film behaves belonged to nobody's test.
 *
 * `posterUri` is what separates the two books a film cover can be: one published
 * with a still derived from the film (what publishing writes now), and one
 * published before that field existed, which has no picture of itself anywhere but
 * the film.
 */
export function withFilmCover(
  book: FlashBookModel,
  videoStartMs = 0,
  posterUri?: string,
): FlashBookModel {
  return {
    ...book,
    cover: {
      kind: 'video',
      videoUri: FIXTURE_COVER_FILM_URI,
      videoStartMs,
      ...(posterUri ? {videoThumbnailUri: posterUri} : {}),
    },
  }
}

/**
 * A read of a book, as if it had already happened, for the report.
 *
 * The reader's ending is the one surface a browser cannot reach the honest way:
 * getting there means answering ten different question widgets, across nine
 * themes, and every one of them has to be driven through its own controls. So the
 * fixture seeds the *results* instead — built from the book's own blocks, so the
 * correct answers, the type labels and the page titles the report prints are the
 * real ones rather than strings written for a test.
 *
 * The pattern is fixed rather than random: one exercise in four missed, one in
 * six half-right, one in five never reached, the rest right. That gives every
 * part of a report something to show — a weakness, a partial score, a skip, a
 * streak, a pace — without a seed, so a run says the same thing twice.
 */
export function buildReportSeed(pages: readonly FlashBookPage[]): {
  pageResults: FlashBookPageResult[]
  elapsedMs: number
} {
  const pageResults: FlashBookPageResult[] = []
  let exercise = 0
  let reading = 0

  pages.forEach((page, pageIndex) => {
    const blocks: FlashBookPageResult['blocks'] = []
    for (const block of page.blocks) {
      if (block.type === 'content') {
        // Recordings are reported the moment they are played, and the fixture
        // leaves the last one unheard so the report has a listening gap to name.
        reading += 1
        if (reading % 4 !== 0) {
          blocks.push({
            blockId: block.id,
            type: block.type,
            isCorrect: true,
            points: 0,
            timeSpentMs: 8_000 + pageIndex * 900,
          })
        }
        continue
      }
      if (NON_SCORED_TYPES.has(block.type)) continue

      exercise += 1
      const verdict =
        exercise % 5 === 3
          ? 'skipped'
          : exercise % 6 === 2
            ? 'partial'
            : exercise % 4 === 0
              ? 'wrong'
              : 'right'
      if (verdict === 'skipped') continue

      // Scored against the block's own maximum, the way the player scores it — a
      // right answer earns all of it. Inventing a fixed two points here made the
      // fixture's report contradict itself: "Perfect read" over a score of 20%.
      const maxPoints = getSlideMaxPoints(block)
      blocks.push({
        blockId: block.id,
        type: block.type,
        isCorrect: verdict === 'right',
        // A partial scores something, because that is the difference between a
        // half-right answer and a blank one — and the report draws them
        // differently.
        points:
          verdict === 'right'
            ? maxPoints
            : verdict === 'partial'
              ? Math.max(1, Math.round(maxPoints / 2))
              : 0,
        userAnswer: wrongAnswerFor(block),
        timeSpentMs: 4_000 + exercise * 700,
      })
    }
    pageResults.push({
      pageId: page.id,
      timeSpentMs: 12_000 + pageIndex * 2_500,
      blocks,
    })
  })

  return {
    pageResults,
    // A plausible read rather than a sum of the times above: the report's own
    // clock is the sitting, not the page arithmetic.
    elapsedMs: 6 * 60_000 + 32_000,
  }
}

/**
 * The six learners a class-report fixture is built from.
 *
 * Named rather than numbered, because the whole claim a class report makes is
 * "*these people* missed this question" — a fixture of `learner-1` would let a
 * report that printed the wrong name pass. The DIDs are `did:plc:` plus exactly
 * twenty-four characters, the shape the fixture's stand-in session uses.
 */
export const CLASS_FIXTURE_LEARNERS = [
  {tag: 'amina', name: 'Amina Belhaj'},
  {tag: 'bilal', name: 'Bilal Karim'},
  {tag: 'chloe', name: 'Chloé Marchand'},
  {tag: 'dina', name: 'Dina Haddad'},
  {tag: 'elias', name: 'Elias Nasri'},
  {tag: 'fatima', name: 'Fatima Zahra'},
] as const

export const classFixtureDid = (tag: string): string =>
  `did:plc:${`${tag}000000000000000000000000`.slice(0, 24)}`

/**
 * A class's worth of plays of a book, for the class report.
 *
 * Built from the *book*, like the report seed beside it, so the fixture names the
 * questions and answers that are actually on the page rather than a second,
 * test-only copy of them — a class report measured against invented questions
 * would prove nothing about the one that ships.
 *
 * The pattern is fixed, and deliberately shaped so the report has a definite
 * ranking to be measured against:
 *
 *  • The **first scored question** is the class's hardest, missed by exactly four
 *    of the six (`4 of 6 missed`) — it sits on the first page, so every learner
 *    reaches it and the count cannot move with the book's length.
 *  • A question every fifth one after that is **split** — missed by three, which is
 *    exactly half the class — so the "have the half who got it explain it" insight
 *    has something true to stand on.
 *  • A question is missed by one learner, and a sixth of them are **part right**,
 *    so the miss list draws both kinds of mark.
 *  • **Fatima stops a third of the way in**, which is what gives the report a
 *    completion figure below 100 and a learner who did the work they reached but
 *    needs catching up.
 *  • **Nobody reads the final page.** The whole class stopped one page short, which
 *    is what puts real entries in the report's `unreached` list — the questions a
 *    teacher must be told nobody tried, rather than told they failed.
 *
 * A book of one or two pages is read whole: there is no "one page short" to leave
 * unread, and a fixture that made every question unreached would be measuring the
 * empty state rather than the report.
 */
export function buildClassPlays(pages: readonly FlashBookPage[]): ClassPlay[] {
  const [, , , dina, , fatima] = CLASS_FIXTURE_LEARNERS
  const lastPageForFatima = Math.max(1, Math.ceil(pages.length / 3))
  /** The page the class as a whole got to, and no further. */
  const workStopsBefore = Math.max(2, pages.length - 1)
  /** The book's first scored question — the one the class as a whole misses. */
  let hardBlockId: string | null = null

  const plays: ClassPlay[] = CLASS_FIXTURE_LEARNERS.map(
    (learner, learnerIndex) => {
      const pageResults: FlashBookPageResult[] = []
      let exercise = -1

      pages.forEach((page, pageIndex) => {
        // The learner who stopped: no results at all past the page they left off on,
        // which is how a report tells "did not get there" from "got it wrong".
        if (
          pageIndex >=
          (learner === fatima ? lastPageForFatima : workStopsBefore)
        )
          return

        const blocks: FlashBookPageResult['blocks'] = []
        for (const block of page.blocks) {
          if (block.type === 'content') {
            blocks.push({
              blockId: block.id,
              type: block.type,
              isCorrect: true,
              points: 0,
              timeSpentMs: 7_000 + learnerIndex * 400,
            })
            continue
          }
          if (NON_SCORED_TYPES.has(block.type)) continue

          exercise += 1
          if (exercise === 0) hardBlockId = block.id
          const missed = missedBy(exercise)
          // One learner half-right on every sixth question, so the miss list has both
          // kinds of mark on it. Per *learner*, which is the whole point: a rule that
          // did not name her would mark the entire class wrong on that question.
          const partial =
            learner === dina && exercise % 6 === 2 && !missed.includes(dina.tag)
          const wrong = missed.includes(learner.tag) || partial
          const maxPoints = getSlideMaxPoints(block)

          blocks.push({
            blockId: block.id,
            type: block.type,
            isCorrect: !wrong,
            points: !wrong
              ? maxPoints
              : partial
                ? Math.max(1, Math.round(maxPoints / 2))
                : 0,
            userAnswer: wrong
              ? classWrongAnswerFor(block, learnerIndex)
              : rightAnswerFor(block),
            timeSpentMs: 3_500 + exercise * 600 + learnerIndex * 300,
          })
        }

        pageResults.push({
          pageId: page.id,
          timeSpentMs: 10_000 + pageIndex * 2_000,
          blocks,
        })
      })

      return {
        learnerDid: classFixtureDid(learner.tag),
        learnerName: learner.name,
        learnerHandle: `${learner.tag}.test`,
        pageResults,
        timeMs: 5 * 60_000 + learnerIndex * 45_000,
      }
    },
  )

  /*
   * Amina played it twice.
   *
   * Her earlier attempt is *prepended*, so the list is in time order and the play
   * the class report is about is her latest one — the one above, where she got the
   * hard question right. This is what makes "one learner, one attempt" measurable
   * in a browser rather than only in a unit test: if the report counted the earlier
   * attempt, the hardest question would read `5 of 6 missed` instead of `4 of 6`,
   * and Amina's own score would drop. Nothing else about the class changes.
   */
  const latest = plays[0]
  const earlier: ClassPlay = {
    ...latest,
    pageResults: latest.pageResults.map(page => ({
      ...page,
      blocks: page.blocks.map(block =>
        block.blockId === hardBlockId
          ? {...block, isCorrect: false, points: 0, userAnswer: undefined}
          : block,
      ),
    })),
  }

  return [earlier, ...plays]
}

/**
 * Who missed the question at `exercise` (0 = the first scored question).
 *
 * One definition, read by the learner loop above, so the pattern cannot drift into
 * two answers about the same question.
 */
function missedBy(exercise: number): string[] {
  const [, bilal, chloe, dina, elias, fatima] = CLASS_FIXTURE_LEARNERS
  if (exercise === 0) return [bilal.tag, chloe.tag, elias.tag, fatima.tag]
  if (exercise % 5 === 1) return [bilal.tag, chloe.tag, dina.tag]
  if (exercise % 3 === 0) return [bilal.tag]
  return []
}

/** The right answer, for the fixture's learners who got the question. */
function rightAnswerFor(block: Slide): unknown {
  switch (block.type) {
    case 'single_choice':
    case 'multiple_choice':
    case 'image_choice':
      return block.options?.find(o => o.isCorrect)?.id
    case 'true_false':
      return block.answer
    case 'fill_blanks':
      return block.fillBlanksAnswers?.map(a => a.answer)
    case 'complex_fill_blank':
      return block.complexBlanks?.map(b => b.answer)
    case 'ordering':
      return block.orderItems?.map(i => i.id)
    case 'matching':
      return Object.fromEntries(
        (block.pairs ?? []).map(pair => [pair.id, pair.right]),
      )
    default:
      return undefined
  }
}

/**
 * A wrong answer the report can *print*.
 *
 * `wrongAnswerFor` beside the report seed covers the choice-shaped blocks — the ones
 * whose wrong answer is another option. A class report's payload is what each learner
 * actually wrote, so a fixture that answered every other kind with `undefined` left
 * the miss rows saying a name and a dash: a report that names who was wrong but
 * cannot show *how* is the half of the feature that matters least.
 *
 * Each of these is the mistake the widget would really record — the two answers the
 * right way round but swapped, a filled-in word that is not the one asked for — and
 * in the shape `formatUserAnswer` renders, so the fixture cannot invent an encoding
 * the app does not use.
 */
function classWrongAnswerFor(block: Slide, learnerIndex: number): unknown {
  switch (block.type) {
    case 'fill_blanks': {
      const answers = (block.fillBlanksAnswers ?? []).map(a => a.answer)
      if (answers.length < 2) return answers.map(() => '?')
      /*
       * The words are all there, out of order — and *differently* out of order per
       * learner.
       *
       * A rotation rather than one fixed wrong answer, because a class where all
       * four learners wrote precisely the same thing is a class of one: the report's
       * value is in seeing which mistake is shared and which is a learner's own. The
       * shift is never a whole turn, so no rotation ever lands on the right answer.
       */
      const shift = (learnerIndex % (answers.length - 1)) + 1
      return [...answers.slice(shift), ...answers.slice(0, shift)]
    }
    case 'complex_fill_blank':
      return block.complexBlanks?.map(b =>
        b.answer.length > 1 ? b.answer.slice(0, 1) : '?',
      )
    case 'ordering':
      return block.orderItems?.map(i => i.id).reverse()
    case 'matching':
      return Object.fromEntries(
        (block.pairs ?? []).map(pair => [
          pair.id,
          (block.pairs ?? []).find(p => p.id !== pair.id)?.right ?? pair.right,
        ]),
      )
    case 'drag_words': {
      const values = [
        ...(block.dragWordsTemplate ?? '').matchAll(/\[([^\]]+)\]/g),
      ].map(m => m[1])
      return values.length > 1 ? [...values].reverse() : values
    }
    /*
     * A word from the exercise that is *not* one of the answers.
     *
     * Taking the first target instead would be the fixture claiming a wrong answer
     * that is in fact right — and on a class report, where the whole point is a
     * teacher reading the wrong answers for a shared misconception, a fabricated one
     * is worse than none.
     */
    case 'find_words': {
      const targets = new Set(block.findWordsTargets ?? [])
      const stray = (block.findWordsGrid ?? [])
        .flat()
        .find(word => !targets.has(word))
      return stray ? [stray] : []
    }
    case 'mark_words': {
      const correct = new Set(block.markWordsCorrect ?? [])
      const stray = (block.markWordsText ?? '')
        .split(/\s+/)
        .find(word => !!word && !correct.has(word))
      return stray ? [stray] : []
    }
    default:
      return wrongAnswerFor(block)
  }
}

/**
 * A book somebody has been reading for a week.
 *
 * The state a resume screen exists for — a couple of reads that ended, one of them
 * better than the other, and a read still open a few pages in — built by *driving
 * the real model* rather than by writing a stored object by hand. A hand-written
 * fixture would let the shape drift from the thing the reader actually stores, and
 * the fixture would then be measuring a book nobody can produce.
 *
 * The open read's answers are the report seed's own, so what a resumed reader sees
 * on the pages behind them is the fixture's real questions and real verdicts.
 */
export function buildProgressSeed({
  bookUri,
  bookTitle,
  pages,
  now = '2026-02-10T18:00:00.000Z',
}: {
  bookUri: string
  bookTitle: string
  pages: readonly FlashBookPage[]
  now?: string
}): FlashBookReadProgress {
  const full = buildReportSeed(pages).pageResults
  const minutesBefore = (minutes: number) =>
    new Date(Date.parse(now) - minutes * 60_000).toISOString()
  const lastPage = Math.max(0, pages.length - 1)

  /*
   * Nine days ago: a read that stopped on the first page, and scored less for it.
   *
   * It answers one page of the book, and is registered unfinished because that is
   * what it was — which is also what *makes* it the bottom of a progression. It
   * used to answer every page but the last two, and that made the history a lie:
   * this book's scoring exercises all live on its opening pages, so dropping the
   * tail dropped no points and both reads came out at the same 58%. A seed whose
   * two reads tie cannot tell a learner's progress from a learner's habits.
   */
  let progress = beginRead(emptyProgress(bookUri, bookTitle), {
    pages,
    now: minutesBefore(9 * 24 * 60),
  })
  progress = finishRead(progress, {
    sessionId: progress.open!.id,
    pages,
    pageResults: full.slice(0, 1),
    timeMs: 4 * 60_000,
    pageReached: 0,
    completed: false,
    now: minutesBefore(9 * 24 * 60 - 22),
  })

  // Two days ago: read to the end, which is the score the cover should call best.
  progress = beginRead(progress, {pages, now: minutesBefore(2 * 24 * 60)})
  progress = finishRead(progress, {
    sessionId: progress.open!.id,
    pages,
    pageResults: full,
    timeMs: 7 * 60_000,
    pageReached: lastPage,
    now: minutesBefore(2 * 24 * 60 - 25),
  })

  // And the read that never ended: three pages in, which is the one to continue.
  progress = beginRead(progress, {pages, now: minutesBefore(35)})
  progress = saveReadPosition(progress, {
    sessionId: progress.open!.id,
    pageIndex: Math.min(2, lastPage),
    pageResults: full.slice(0, 2),
    timeMs: 3 * 60_000,
    now,
  })
  return progress
}

/**
 * One book on the device's shelf of books part-way through.
 *
 * The shelf is the one surface whose whole content is *device state* — the books a
 * learner is in the middle of are a fact about the machine in their hand, not about
 * any record — so a browser cannot reach it by reading anything: the index has to be
 * seeded with several books at once, each at a different page and each read at a
 * different time, or the ordering the shelf exists to get right is unmeasurable.
 *
 * Built by driving the real model (`beginRead` / `saveReadPosition` / `finishRead`)
 * rather than by hand-writing a stored object, for the reason every seed here is:
 * a hand-written one lets the shape drift from what the reader stores, and the
 * fixture then measures a shelf nobody can produce.
 *
 * `finished` is the other half of the claim: a book whose read *ended* belongs on
 * the shelf no more than a book never opened, and the only way to show that is to
 * seed one and check it is absent.
 */
export function buildShelfRead({
  bookUri,
  bookTitle,
  pages,
  pageIndex,
  lastReadAt,
  finished = false,
}: {
  bookUri: string
  bookTitle: string
  pages: readonly FlashBookPage[]
  pageIndex: number
  lastReadAt: string
  finished?: boolean
}): FlashBookReadProgress {
  const full = buildReportSeed(pages).pageResults
  const lastPage = Math.max(0, pages.length - 1)
  let progress = beginRead(emptyProgress(bookUri, bookTitle), {
    pages,
    now: new Date(Date.parse(lastReadAt) - 30 * 60_000).toISOString(),
  })
  if (finished) {
    return finishRead(progress, {
      sessionId: progress.open!.id,
      pages,
      pageResults: full,
      timeMs: 6 * 60_000,
      pageReached: lastPage,
      now: lastReadAt,
    })
  }
  return saveReadPosition(progress, {
    sessionId: progress.open!.id,
    pageIndex: Math.min(pageIndex, lastPage),
    pageResults: full.slice(0, Math.min(pageIndex, lastPage)),
    timeMs: 4 * 60_000,
    now: lastReadAt,
  })
}

/**
 * The place a *different* device has left in the same book.
 *
 * Cross-device reading is the one thing a browser cannot produce for itself: the
 * record has to exist on the learner's repo before there is anything to take up,
 * so without a seed of this shape the adopt path — and the whole question of which
 * copy wins — could be described but never measured.
 *
 * Deliberately **not** the place `buildProgressSeed` leaves the reader at. That one
 * is three pages in and this one is two, and they are reads of their own with their
 * own ids: two seeds that agreed could not tell "the device's copy won" apart from
 * "nothing happened at all", which is the distinction the feature turns on.
 */
export function buildRemoteReadSeed({
  bookUri,
  bookTitle,
  pages,
  now = '2026-02-12T09:00:00.000Z',
}: {
  bookUri: string
  bookTitle: string
  pages: readonly FlashBookPage[]
  now?: string
}): FlashBookReadProgress {
  const full = buildReportSeed(pages).pageResults
  const lastPage = Math.max(0, pages.length - 1)
  let progress = beginRead(emptyProgress(bookUri, bookTitle), {
    pages,
    now: '2026-02-12T08:45:00.000Z',
  })
  progress = saveReadPosition(progress, {
    sessionId: progress.open!.id,
    pageIndex: Math.min(1, lastPage),
    pageResults: full.slice(0, 2),
    timeMs: 9 * 60_000,
    now,
  })
  return progress
}

/**
 * Something a learner might plausibly have picked, for a seeded wrong answer.
 *
 * Only the choice types have an answer the report can name — the rest are left
 * unanswered-but-attempted, which is what a widget that never reported its value
 * looks like, and the report prints its own dash for that.
 */
function wrongAnswerFor(block: Slide): unknown {
  switch (block.type) {
    case 'single_choice':
    case 'multiple_choice':
    case 'image_choice':
      return block.options?.find(o => !o.isCorrect)?.id
    case 'true_false':
      return block.answer === 'true' ? 'false' : 'true'
    default:
      return undefined
  }
}

/** A whole book that exercises every renderable block type. */
export function buildFlashBookFixture(themeId = 'storybook'): FlashBookModel {
  return {
    id: 'fixture-flashbook',
    title: 'Every block type',
    description: 'A fixture book used by the browser-driven layout tests.',
    creatorId: 'did:plc:flashbook-fixture',
    themeId,
    status: 'draft',
    cover: {kind: 'image', imageUri: PLACEHOLDER_IMAGE},
    pages: buildFixturePages(),
    behaviorSettings: {},
    metadata: {
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
    likeCount: 0,
    likedByMe: false,
  }
}
